import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Download, Eye, LogOut, Calendar, MapPin, Briefcase, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import ApplicantDetailModal from '../components/ApplicantDetailModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  'All Statuses',
  'New',
  'Contacted',
  'Screening Scheduled',
  'Qualified',
  'Submitted to Client',
  'Placed',
  'Not a Fit',
  'On Hold'
];

const STATUS_COLORS = {
  'New': 'bg-blue-100 text-blue-800',
  'Contacted': 'bg-purple-100 text-purple-800',
  'Screening Scheduled': 'bg-yellow-100 text-yellow-800',
  'Qualified': 'bg-green-100 text-green-800',
  'Submitted to Client': 'bg-indigo-100 text-indigo-800',
  'Placed': 'bg-emerald-100 text-emerald-800',
  'Not a Fit': 'bg-red-100 text-red-800',
  'On Hold': 'bg-gray-100 text-gray-800'
};

const UNAUTHORIZED = 401;

const AdminApplicants = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [applicants, setApplicants] = useState([]);
  const [filteredApplicants, setFilteredApplicants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Statuses');
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check for admin token
    const adminToken = sessionStorage.getItem('s2s_admin_token');
    if (!adminToken) {
      navigate('/admin/login');
      return;
    }
    setToken(adminToken);
    fetchApplicants(adminToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchApplicants = async (adminToken) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/applicants`, {
        params: { token: adminToken, limit: 1000 }
      });
      
      if (response.data.success) {
        setApplicants(response.data.applicants);
        setFilteredApplicants(response.data.applicants);
        setTotal(response.data.total);
      }
    } catch (error) {
      if (error.response?.status === UNAUTHORIZED) {
        sessionStorage.removeItem('s2s_admin_token');
        navigate('/admin/login');
      } else {
        setError('Failed to load applicants. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Apply filters
    let filtered = applicants;

    if (searchTerm) {
      filtered = filtered.filter(app => 
        app.fullLegalName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.phoneNumber?.includes(searchTerm)
      );
    }

    if (statusFilter !== 'All Statuses') {
      filtered = filtered.filter(app => app.status === statusFilter);
    }

    setFilteredApplicants(filtered);
  }, [searchTerm, statusFilter, applicants]);

  const handleLogout = () => {
    sessionStorage.removeItem('s2s_admin_token');
    navigate('/admin/login');
  };

  const handleViewDetail = async (applicantId) => {
    try {
      const response = await axios.get(`${API}/admin/applicants/${applicantId}`, {
        params: { token }
      });
      
      if (response.data.success) {
        setSelectedApplicant(response.data.applicant);
        setShowDetailModal(true);
      }
    } catch (error) {
      setError('Failed to load applicant details. Please try again.');
    }
  };

  const handleApplicantUpdate = () => {
    // Refresh applicants list after update
    fetchApplicants(token);
    setShowDetailModal(false);
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/applicants/export/csv`, {
        params: { token },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `applicants_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setError('Failed to export CSV. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#0a1628] text-white border-b-4 border-[#d4a574]">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">Applicant Tracking System</h1>
              <p className="text-gray-300 mt-1">Service2Success Talent Solutions</p>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-[#d4a574] text-white hover:bg-[#d4a574] hover:text-[#0a1628]"
            >
              <LogOut className="mr-2" size={18} />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center justify-between" data-testid="admin-error-banner">
            <span>{error}</span>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 font-bold">×</button>
          </div>
        )}

        {/* Filters and Actions */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="grid md:grid-cols-4 gap-4">
              {/* Search */}
              <div className="md:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <Input
                    type="text"
                    placeholder="Search by name, email, or phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  onClick={() => fetchApplicants(token)}
                  variant="outline"
                  className="flex-1"
                >
                  <RefreshCw size={18} />
                </Button>
                <Button
                  onClick={handleExportCSV}
                  className="flex-1 bg-[#d4a574] text-[#0a1628] hover:bg-[#c49564]"
                >
                  <Download className="mr-2" size={18} />
                  CSV
                </Button>
              </div>
            </div>

            {/* Stats */}
            <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
              <span>
                Showing {filteredApplicants.length} of {total} applicants
              </span>
              <span>
                {statusFilter !== 'All Statuses' && `Filtered by: ${statusFilter}`}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Applicants Table */}
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Loading applicants...</p>
          </div>
        ) : filteredApplicants.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-gray-600 text-lg">No applicants found</p>
              <p className="text-gray-500 text-sm mt-2">Try adjusting your search or filters</p>
            </CardContent>
          </Card>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#0a1628] text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Date Applied</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Name</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Contact</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Location</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Skill Set</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Relocation</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredApplicants.map((applicant) => (
                    <tr key={applicant.applicantId} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div className="flex items-center">
                          <Calendar size={16} className="mr-2 text-gray-400" />
                          {formatDate(applicant.submissionDate)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900">{applicant.fullLegalName}</div>
                        {applicant.preferredName && (
                          <div className="text-xs text-gray-500">({applicant.preferredName})</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="text-gray-900">{applicant.email}</div>
                        <div className="text-gray-500">{applicant.phoneNumber}</div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center text-gray-700">
                          <MapPin size={14} className="mr-1 text-gray-400" />
                          {applicant.cityState || 'N/A'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center text-gray-700">
                          <Briefcase size={14} className="mr-1 text-gray-400" />
                          <span className="truncate max-w-[200px]">
                            {applicant.desiredJobTitles || 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          applicant.openToRelocation === 'yes' 
                            ? 'bg-green-100 text-green-800' 
                            : applicant.openToRelocation === 'no'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {applicant.openToRelocation === 'yes' ? 'Yes' : applicant.openToRelocation === 'no' ? 'No' : 'Maybe'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[applicant.status] || 'bg-gray-100 text-gray-800'}`}>
                          {applicant.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button
                          onClick={() => handleViewDetail(applicant.applicantId)}
                          size="sm"
                          className="bg-[#d4a574] text-[#0a1628] hover:bg-[#c49564]"
                        >
                          <Eye size={16} className="mr-1" />
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedApplicant && (
        <ApplicantDetailModal
          applicant={selectedApplicant}
          token={token}
          onClose={() => setShowDetailModal(false)}
          onUpdate={handleApplicantUpdate}
        />
      )}
    </div>
  );
};

export default AdminApplicants;
